/* eslint-disable no-console */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo } from "react";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import { TIME_FILTER } from "../../Modules/Hubs/constants";

const Tooltip = ({ slice }) => {
  const { points } = slice.slice;

  const getTimestamp = () => {
    if (!points?.length) return "";

    if (points[0]?.data?.timestamp)
      return dayjs(points[0]?.data?.timestamp).format("MMM DD, YYYY, hh:mm a");

    if (points[0]?.data?.x)
      return `${points[0]?.data?.x} ${points[0]?.data?.unit}`;

    return "";
  };

  return (
    <div
      className="flex flex-col gap-y-2 rounded-lg bg-white px-2 py-1"
      style={{
        width: "155px",
        boxShadow: "2px 2px 4px 0px rgba(121, 121, 121, 0.12)",
      }}
    >
      <p className="font-aileron text-10 font-medium text-dark-gray">
        {getTimestamp()}
      </p>

      {points.map((point) => {
        const energy = point.data.y;

        if (!point.data.y) return null;

        return (
          <div key={point.id} className="flex items-center gap-x-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: point.color }}
            />

            <p className="flex-1 font-aileron text-xs font-normal text-dark-gray">
              {point?.seriesId}
            </p>

            <p className="font-aileron text-xs font-semibold text-[#000C1E]">
              {parseFloat(energy).toFixed(1)} kWh
            </p>
          </div>
        );
      })}
    </div>
  );
};

const LineChart = ({ data, timeFilter }) => {
  // formatting the x value to convert into date and filtering out null values
  const formatted = useMemo(() => {
    return data?.map((series) => ({
      ...series,
      data: series.data
        ?.map((point) => {
          const date = new Date(point.x);
          return Number.isNaN(date.getTime()) ? null : { ...point, x: date };
        })
        .filter(Boolean),
    }));
  }, [data]);

  // if the selected filter is today or yesterday -> show only time
  // if anything else -> show only date
  const getFormattedValue = (value) => {
    if (!value) return 0;

    if (
      timeFilter?.id === TIME_FILTER.TODAY ||
      timeFilter?.id === TIME_FILTER.YESTERDAY
    ) {
      return dayjs(value).format("hh:mm a");
    }

    return dayjs(value).format("DD MMM, YYYY");
  };

  // to calculate y range dynamically
  // const rawMax =
  //   data?.length &&
  //   data[0] &&
  //   Array.isArray(data[0].data) &&
  //   data[0].data?.length
  //     ? Math.max(...data[0].data.map((d) => +d.y))
  //     : 0;

  const yValues =
    data?.[0]?.data?.map((d) => Number(d.y)).filter((n) => !Number.isNaN(n)) ||
    [];

  const rawMax = yValues?.length ? Math.max(...yValues) : 0;

  // Add padding buffer — 10% of value range
  const buffer = rawMax * 0.2;
  const roundedMax = Math.ceil(((rawMax + buffer) / 100) * 100);

  // Calculate 4 grid lines
  const step = roundedMax / 4;

  const gridYValues =
    rawMax === 0
      ? [0, 5, 10, 15, 20]
      : [0, step, step * 2, step * 3, roundedMax];

  return (
    <div className="w-full h-full relative">
      <ResponsiveLine
        data={formatted}
        margin={{ top: 20, right: 45, bottom: 40, left: 40 }}
        yScale={{
          type: "linear",
          min: 0,
          max: Math.max(...gridYValues),
          reverse: false,
          stacked: false,
        }}
        xScale={{ type: "point" }}
        axisBottom={{ format: (value) => getFormattedValue(value) }}
        useMesh
        enableGridY
        enableGridX={false}
        axisLeft={{ tickValues: gridYValues }}
        colors={{ datum: "color" }}
        enableSlices="x"
        enableTouchCrosshair
        tooltip={Tooltip}
        sliceTooltip={(slice) => <Tooltip slice={slice} />}
        pointSize={8}
        theme={{
          text: {
            fontSize: 10,
            fontFamily: "aileron",
            fill: "#525252",
            outlineWidth: 0,
            outlineColor: "transparent",
          },
          axis: {
            ticks: {
              line: {
                stroke: "#525252",
                strokeWidth: 0,
              },
              text: {
                fontSize: 10,
                fontFamily: "aileron",
                fill: "#525252",
                outlineWidth: 0,
                outlineColor: "transparent",
              },
            },
          },
          grid: {
            line: {
              stroke: "#DFDFDF",
              strokeWidth: 1,
              strokeDasharray: 6,
            },
          },
        }}
      />

      {/* y-axis label */}
      <div className="absolute top-[45%] -left-9 -rotate-90">
        <p className="font-aileron text-10 font-normal text-dark-gray">
          Energy (kWh)
        </p>
      </div>

      {/* x-axis label */}
      <div className="absolute -bottom-4 left-[45%] flex items-center justify-center gap-x-4">
        {data?.map((item) => {
          return (
            <div key={item.id} className="flex items-center gap-x-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />

              <p className="font-aileron text-xs font-normal text-dark-gray">
                {item.id}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LineChart;
